// Web — OG / Twitter social preview admin page.
//
// ISSUE-42 — `POST /api/v1/admin/web/og-preview` BE'de hazır; tip + slug
//            verince meta etiketler server tarafında üretilip dönüyor.
//
// Mobile/desktop responsive: form `layout="inline"` + wrap, sonuç kartları
// `<Col xs={24} md={12}>` ile mobilde alt alta yığılır.

import { useState } from 'react';
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import { webApi } from '@api/web';
import { articlesApi } from '@api/articles';
import { pagesApi } from '@api/pages';
import { sssApi } from '@api/sss';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import {
  OG_PREVIEW_CONTENT_TYPES,
  type OgPreview,
  type OgPreviewContentTypeName,
} from '@features/web/types';

const { Text, Paragraph } = Typography;

interface TargetOption {
  value: string;
  label: string;
}

// Her tür için seçilebilir içerikleri (başlık + slug) döndürür.
// Sss'te slug kolonu yoktur → BE entity Guid'ini "slug" olarak kabul eder,
// bu yüzden değer olarak id kullanılır, etikette soru gösterilir.
async function loadTargets(type: OgPreviewContentTypeName): Promise<TargetOption[]> {
  switch (type) {
    case 'Article': {
      const { items } = await articlesApi.list({ pageSize: 200 });
      return items
        .filter((i) => i.slug)
        .map((i) => ({ value: i.slug, label: `${i.title || i.slug} — /${i.slug}` }));
    }
    case 'Page': {
      const { items } = await pagesApi.list({ pageSize: 200 });
      return items
        .filter((i): i is typeof i & { slug: string } => Boolean(i.slug))
        .map((i) => ({ value: i.slug, label: `${i.title || i.slug} — /${i.slug}` }));
    }
    case 'Sss': {
      const { items } = await sssApi.list({ pageSize: 200 });
      return items.map((i) => ({ value: i.id, label: i.question }));
    }
    default:
      return [];
  }
}

function OgPreviewSection() {
  const { t } = useTranslation(['web', 'common', 'errors']);
  const td = t as unknown as (key: string) => string;
  const [form] = Form.useForm<{ type: OgPreviewContentTypeName; slug: string }>();
  const [preview, setPreview] = useState<OgPreview | null>(null);

  const selectedType =
    (Form.useWatch('type', form) as OgPreviewContentTypeName | undefined) ?? 'Article';
  const targetsQuery = useQuery({
    queryKey: ['og-targets', selectedType],
    queryFn: () => loadTargets(selectedType),
    staleTime: 60_000,
  });

  const previewMut = useMutation({
    mutationFn: ({ type, slug }: { type: OgPreviewContentTypeName; slug: string }) =>
      webApi.ogPreview.build({ type: OG_PREVIEW_CONTENT_TYPES[type], slug }),
    onSuccess: (dto) => setPreview(dto),
    onError: (err) => void message.error(resolveApiErrorMessage(err)),
  });

  const handleSubmit = (values: { type: OgPreviewContentTypeName; slug: string }) => {
    setPreview(null);
    previewMut.mutate(values);
  };

  return (
    <Card title={t('og.cardTitle')} data-testid="og-preview-section">
      <Form
        form={form}
        layout="inline"
        onFinish={handleSubmit}
        initialValues={{ type: 'Article' }}
        onValuesChange={(changed) => {
          // Tür değişince önceki içerik seçimini temizle (farklı türün slug'ı geçersiz).
          if ('type' in changed) form.setFieldValue('slug', undefined);
        }}
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
      >
        <Form.Item
          name="type"
          label={t('og.typeLabel')}
          rules={[{ required: true }]}
          style={{ marginBottom: 8 }}
        >
          <Select
            options={(Object.keys(OG_PREVIEW_CONTENT_TYPES) as OgPreviewContentTypeName[]).map(
              (k) => ({ value: k, label: td(`og.contentType.${k}`) }),
            )}
            style={{ width: 160 }}
            data-testid="og-type"
          />
        </Form.Item>
        <Form.Item
          name="slug"
          label={t('og.slugLabel')}
          rules={[{ required: true, message: t('og.slugRequired') }]}
          style={{ flex: 1, minWidth: 240, marginBottom: 8 }}
        >
          <Select
            showSearch
            optionFilterProp="label"
            loading={targetsQuery.isLoading}
            options={targetsQuery.data ?? []}
            placeholder={t('og.slugPlaceholder')}
            notFoundContent={
              targetsQuery.isLoading ? <Spin size="small" /> : t('og.targetEmpty')
            }
            style={{ width: '100%' }}
            data-testid="og-slug"
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 8 }}>
          <Button
            type="primary"
            htmlType="submit"
            icon={<Send size={14} />}
            loading={previewMut.isPending}
            data-testid="og-preview-btn"
          >
            {t('og.previewButton')}
          </Button>
        </Form.Item>
      </Form>

      {preview ? (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card size="small" title={t('og.openGraphTitle')} data-testid="og-card">
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>og:title</Text>
                  <div style={{ fontWeight: 600 }}>{preview.ogTitle || '—'}</div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>og:description</Text>
                  <Paragraph style={{ marginBottom: 4 }}>{preview.ogDescription || '—'}</Paragraph>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>og:type</Text>{' '}
                  <Tag>{preview.ogType}</Tag>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>og:url</Text>
                  <div>
                    <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{preview.ogUrl}</code>
                  </div>
                </div>
                {preview.ogImage ? (
                  <img
                    src={resolveAssetUrl(preview.ogImage)}
                    alt=""
                    style={{ maxWidth: '100%', borderRadius: 6, marginTop: 8 }}
                  />
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>{t('og.noImage')}</Text>
                )}
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" title={t('og.twitterTitle')} data-testid="twitter-card">
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>twitter:card</Text>{' '}
                  <Tag>{preview.twitterCard}</Tag>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>twitter:title</Text>
                  <div style={{ fontWeight: 600 }}>{preview.twitterTitle || '—'}</div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>twitter:description</Text>
                  <Paragraph style={{ marginBottom: 4 }}>
                    {preview.twitterDescription || '—'}
                  </Paragraph>
                </div>
                {preview.twitterImage ? (
                  <img
                    src={resolveAssetUrl(preview.twitterImage)}
                    alt=""
                    style={{ maxWidth: '100%', borderRadius: 6, marginTop: 8 }}
                  />
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>{t('og.noImage')}</Text>
                )}
              </Space>
            </Card>
          </Col>
          <Col xs={24}>
            <Card size="small" title={t('og.htmlMetaTitle')}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>title</Text>
                  <div style={{ fontWeight: 600 }}>{preview.title || '—'}</div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>description</Text>
                  <Paragraph>{preview.description || '—'}</Paragraph>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>canonical</Text>
                  <div>
                    <code style={{ fontSize: 12, wordBreak: 'break-all' }}>
                      {preview.canonical}
                    </code>
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      ) : (
        <Empty description={t('og.empty')} />
      )}
    </Card>
  );
}

export function OgPreviewPage() {
  const { t } = useTranslation(['web', 'common']);
  return (
    <div>
      <PageHeader
        title={t('og.page.title')}
        description={t('og.page.description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: 'Web' },
          { label: t('og.page.breadcrumb') },
        ]}
      />
      <OgPreviewSection />
    </div>
  );
}

export default OgPreviewPage;
